import type { Router, RouteRecordRaw } from 'vue-router';

import { usePermissionStoreWithOut } from '/@/store/modules/permission';
import { useUserStoreWithOut } from '/@/store/modules/user';

import { PAGE_NOT_FOUND_ROUTE } from '/@/router/routes/basic';
import { RouteEnum } from '/@/enums/routeEnum';

// import { RootRoute } from '/@/router/routes';

// const ROOT_PATH = RootRoute.path;

const whitePathList: RouteEnum[] = [
  RouteEnum.BASE_LOGIN,
  RouteEnum.RESET_PAGE,
  RouteEnum.GOOGLE_REDIRECT,
  RouteEnum.JOIN_TEAM_BRIDGE,
  RouteEnum.EMAIL_AUTHENTICATION,
];

export function createPermissionGuard(router: Router) {
  const userStore = useUserStoreWithOut();
  const permissionStore = usePermissionStoreWithOut();
  router.beforeEach(async (to, from, next) => {
    const token = userStore.getToken;
    const info = userStore.getUserInfo;
    const allCodeList = permissionStore.getPermCodeList as string[];
    // console.log(allCodeList);

    // 判断权限
    const { meta } = to;
    const hasPermission =
      !meta?.permCode || allCodeList.some((item) => meta?.permCode?.includes(item));
    if (!hasPermission) {
      next(RouteEnum.RECENTS);
      return;
    }

    // redirect lo
    // Whitelist can be directly entered
    if (whitePathList.includes(to.path as RouteEnum)) {
      if (
        to.path === RouteEnum.BASE_LOGIN &&
        token &&
        info?.team &&
        info?.user.lastLoginTime != undefined
      ) {
        const isSessionTimeout = userStore.getSessionTimeout;
        try {
          await userStore.afterLoginAction();
          if (!isSessionTimeout) {
            next((to.query?.redirect as string) || '/');
            return;
          }
        } catch {}
      }
      next();
      return;
    }

    // token does not exist
    if (!token) {
      // You can access without permission. You need to set the routing meta.ignoreAuth to true
      if (to.meta.ignoreAuth) {
        next();
        return;
      }
      // redirect login page
      const redirectData: { path: string; replace: boolean; query?: Recordable<string> } = {
        path: RouteEnum.BASE_LOGIN,
        replace: true,
      };
      if (to.path) {
        redirectData.query = {
          ...redirectData.query,
          redirect: to.path,
        };
      }
      next(redirectData);
      return;
    }

    if (!info?.team) {
      router.push(RouteEnum.BASE_LOGIN);
    }

    // Jump to the 404 page after processing the login
    if (
      from.path === RouteEnum.BASE_LOGIN &&
      to.name === PAGE_NOT_FOUND_ROUTE.name &&
      to.fullPath !== RouteEnum.RECENTS
    ) {
      next(RouteEnum.RECENTS);
      return;
    }

    // get userinfo while last fetch time is empty
    if (userStore.getLastUpdateTime === 0) {
      try {
        await userStore.getUserInfoAction();
      } catch (err) {
        next();
        return;
      }
    }

    if (permissionStore.getIsDynamicAddedRoute) {
      next();
      return;
    }
    await permissionStore.changePermissionCode();
    const routes = await permissionStore.buildRoutesAction();

    routes.forEach((route) => {
      router.addRoute(route as unknown as RouteRecordRaw);
    });

    router.addRoute(PAGE_NOT_FOUND_ROUTE as unknown as RouteRecordRaw);

    permissionStore.setDynamicAddedRoute(true);
    if (to.name === PAGE_NOT_FOUND_ROUTE.name) {
      // 动态添加路由后，此处应当重定向到fullPath，否则会加载404页面内容
      next({ path: to.fullPath, replace: true, query: to.query });
    } else {
      const redirectPath = (from.query.redirect || to.path) as string;
      const redirect = decodeURIComponent(redirectPath);
      const nextData = to.path === redirect ? { ...to, replace: true } : { path: redirect };
      next(nextData);
    }
  });
}
